import React, { useState } from 'react';
import Plot from 'react-plotly.js';
import api from '../../shared/apiSetup';
import { histLayout } from './Util';

const Analysis = () => {
  // Each element of filterSets represents one histogram panel
  const [filterSets, setFilterSets] = useState([
    {
      experimentId: '',
      deviceType: '',
      language: '',
      ageMin: '',
      ageMax: '',
      loading: false,
      noHistData: false,
      histogramData: {},
    },
  ]);

  // ---------- Helpers ----------
  const atLeastOneFilter = (f) =>
    (f.experimentId && f.experimentId.trim() !== '') ||
    (f.deviceType && f.deviceType !== '') ||
    (f.language && f.language !== '') ||
    (f.ageMin !== '' && f.ageMin !== null) ||
    (f.ageMax !== '' && f.ageMax !== null);

  const ageRangeValid = (f) => {
    if (f.ageMin === '' || f.ageMax === '') return true;
    const min = Number(f.ageMin);
    const max = Number(f.ageMax);
    if (Number.isNaN(min) || Number.isNaN(max)) return false;
    return min <= max;
  };

  const clearPlot = (index) => {
    const updated = [...filterSets];
    updated[index].histogramData = {};
    updated[index].noHistData = false;
    setFilterSets(updated);
  };

  // ---------- API Fetch ----------
  const fetchHistogram = async (index) => {
    const current = filterSets[index];

    if (!atLeastOneFilter(current)) return;
    if (!ageRangeValid(current)) {
      alert('Invalid age range: min should be <= max and both must be numbers.');
      return;
    }

    const updated = [...filterSets];
    updated[index].loading = true;
    updated[index].noHistData = false;
    setFilterSets(updated);

    try {
      const params = {};
      if (current.experimentId && current.experimentId.trim() !== '')
        params.experiment_id = current.experimentId.trim();
      if (current.deviceType) params.device_type = current.deviceType;
      if (current.language) params.language = current.language;
      if (current.ageMin !== '') params.age_min = Number(current.ageMin);
      if (current.ageMax !== '') params.age_max = Number(current.ageMax);

      const response = await api.get('/analysis/exp_analysis_data', { params });

      if (response.data && response.data.noData === true) {
        updated[index].noHistData = true;
        updated[index].histogramData = {};
      } else if (response.data) {
        updated[index].histogramData = {
          data: response.data,
          maxBin: response.data.maxBin,
          highlightedBinIndex: response.data.highlightedBinIndex,
        };
        updated[index].noHistData = false;
      } else {
        updated[index].noHistData = true;
        updated[index].histogramData = {};
      }
    } catch (err) {
      console.error('Error fetching histogram:', err);
      updated[index].noHistData = true;
      updated[index].histogramData = {};
    } finally {
      updated[index].loading = false;
      setFilterSets([...updated]);
    }
  };

  // ---------- Filter Set Controls ----------
  const addFilterSet = () => {
    setFilterSets([
      ...filterSets,
      {
        experimentId: '',
        deviceType: '',
        language: '',
        ageMin: '',
        ageMax: '',
        loading: false,
        noHistData: false,
        histogramData: {},
      },
    ]);
  };

  const removeFilterSet = (index) => {
    const updated = filterSets.filter((_, i) => i !== index);
    setFilterSets(updated);
  };

  // ---------- Rendering ----------
  const getHighlightIndex = (histData) => {
    const hi = histData.highlightedBinIndex;
    return hi != null && !Number.isNaN(Number(hi)) ? Math.ceil(Number(hi)) : -1;
  };

  const globalMaxBin = Math.max(
    0,
    ...filterSets.map((set) => set.histogramData?.maxBin || 0)
  );

  return (
    <div style={{ padding: '1rem' }}>
      <div style={{ width: '100%', overflowX: 'hidden' }}>
        <h2 style={{ marginBottom: '1rem' }}>Histogram Comparison</h2>
        {/* Grid layout for panels */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))',
            gap: '1rem',
          }}
        >
          {filterSets.map((set, index) => {
            const highlightIndex = getHighlightIndex(set.histogramData, globalMaxBin);

            return (
              <div
                key={index}
                style={{
                  border: '1px solid #ccc',
                  borderRadius: 8,
                  padding: '1rem',
                  backgroundColor: '#f9f9f9',
                }}
              >
                <h3>Filter Set {index + 1}</h3>

                {/* Filters */}
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    fetchHistogram(index);
                  }}
                  style={{
                    marginBottom: '1rem',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '0.5rem',
                    alignItems: 'center',
                  }}
                >
                  <div>
                    <label htmlFor={`experimentId-${index}`}>Experiment ID:</label>
                    <input
                      id={`experimentId-${index}`}
                      type="text"
                      value={set.experimentId}
                      onChange={(e) => {
                        const updated = [...filterSets];
                        updated[index].experimentId = e.target.value;
                        setFilterSets(updated);
                        clearPlot(index);
                      }}
                      placeholder="TI-Alpha24"
                      style={{
                        marginLeft: 6,
                        padding: '0.25rem 0.5rem',
                        borderRadius: 4,
                        border: '1px solid #ccc',
                      }}
                    />
                  </div>

                  <div>
                    <label htmlFor={`deviceType-${index}`}>Device:</label>
                    <select
                      id={`deviceType-${index}`}
                      value={set.deviceType}
                      onChange={(e) => {
                        const updated = [...filterSets];
                        updated[index].deviceType = e.target.value;
                        setFilterSets(updated);
                        clearPlot(index);
                      }}
                      style={{
                        marginLeft: 6,
                        padding: '0.25rem 0.5rem',
                        borderRadius: 4,
                        border: '1px solid #ccc',
                      }}
                    >
                      <option value="">-- Any --</option>
                      <option value="desktop">Desktop</option>
                      <option value="mobile">Mobile</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor={`language-${index}`}>Language:</label>
                    <select
                      id={`language-${index}`}
                      value={set.language}
                      onChange={(e) => {
                        const updated = [...filterSets];
                        updated[index].language = e.target.value;
                        setFilterSets(updated);
                        clearPlot(index);
                      }}
                      style={{
                        marginLeft: 6,
                        padding: '0.25rem 0.5rem',
                        borderRadius: 4,
                        border: '1px solid #ccc',
                      }}
                    >
                      <option value="">-- Any --</option>
                      <option value="English">English</option>
                      <option value="Deutsch">Deutsch</option>
                    </select>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    <label>Age:</label>
                    <input
                      type="number"
                      min="0"
                      value={set.ageMin}
                      onChange={(e) => {
                        const updated = [...filterSets];
                        updated[index].ageMin = e.target.value;
                        setFilterSets(updated);
                        clearPlot(index);
                      }}
                      placeholder="min"
                      style={{
                        width: 70,
                        padding: '0.25rem',
                        borderRadius: 4,
                        border: '1px solid #ccc',
                      }}
                    />
                    <span style={{ margin: '0 4px' }}>—</span>
                    <input
                      type="number"
                      min="0"
                      value={set.ageMax}
                      onChange={(e) => {
                        const updated = [...filterSets];
                        updated[index].ageMax = e.target.value;
                        setFilterSets(updated);
                        clearPlot(index);
                      }}
                      placeholder="max"
                      style={{
                        width: 70,
                        padding: '0.25rem',
                        borderRadius: 4,
                        border: '1px solid #ccc',
                      }}
                    />
                  </div>

                  <div>
                    <button
                      type="submit"
                      disabled={set.loading || !atLeastOneFilter(set) || !ageRangeValid(set)}
                      style={{
                        padding: '0.4rem 0.8rem',
                        backgroundColor:
                          !atLeastOneFilter(set) || !ageRangeValid(set)
                            ? '#999'
                            : '#008080',
                        color: 'white',
                        border: 'none',
                        borderRadius: 4,
                        cursor:
                          !atLeastOneFilter(set) || !ageRangeValid(set)
                            ? 'not-allowed'
                            : 'pointer',
                      }}
                    >
                      {set.loading ? 'Loading...' : 'Load'}
                    </button>
                  </div>

                  {filterSets.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeFilterSet(index)}
                      style={{
                        padding: '0.4rem 0.8rem',
                        backgroundColor: '#b33a3a',
                        color: 'white',
                        border: 'none',
                        borderRadius: 4,
                        cursor: 'pointer',
                      }}
                    >
                      Remove
                    </button>
                  )}
                </form>

                {/* Messages */}
                {set.loading && <div>Loading data...</div>}
                {!set.loading && set.noHistData && (
                  <div>No data available for the selected filters.</div>
                )}

                {/* Plot */}
                {set.histogramData?.data?.stats && (
                  <div style={{
                    marginTop: '0.5rem',
                    padding: '0.5rem 0.75rem',
                    backgroundColor: '#eef6f6',
                    borderRadius: 6,
                    fontSize: '0.9rem'
                  }}>
                    <strong>Summary:</strong>{' '}
                    <span>
                      Avg WPM: <b>{set.histogramData.data.stats.avgWPM ?? '—'}</b> | Error Rate:{' '}
                      <b>{set.histogramData.data.stats.avgErrorRate ?? '—'}%</b> | Participants:{' '}
                      {/* | Backspace Rate:{' '} */}
                      {/* <b>{set.histogramData.data.stats.avgBackspaceRate ?? '—'}%</b> */}
                      <b>{set.histogramData.data.stats.totalParticipants}</b>
                    </span>
                  </div>
                )}
                {!set.loading && set.histogramData.data && (
                  <Plot
                    style={{ width: '100%' }}
                    data={[
                      {
                        type: 'bar',
                        x: set.histogramData.data.bin_centers,
                        y: set.histogramData.data.hist,
                        marker: {
                          color: set.histogramData.data.bin_centers.map((_, i) =>
                            i === highlightIndex ? '#008080' : '#FAD5A5'
                          ),
                        },
                        hoverinfo: 'none',
                      },
                    ]}
                    layout={histLayout(set.histogramData, globalMaxBin)}
                    config={{ displayModeBar: false }}
                  />
                )}
              </div>
            );
          })}
        </div>

        {/* Add new set */}
        <button
          onClick={addFilterSet}
          style={{
            marginTop: '1rem',
            padding: '0.5rem 1rem',
            backgroundColor: '#004d4d',
            color: 'white',
            border: 'none',
            borderRadius: 4,
            cursor: 'pointer',
          }}
        >
          + Add Filter Set
        </button>
      </div>
    </div>
  );
};

export default Analysis;
